import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  CircularProgress,
  FormHelperText,
  Grid2 as Grid,
  IconButton,
  InputAdornment,
  Modal,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import {
  MdOutlineCloudUpload,
  MdOutlineDeleteOutline,
  MdVerified,
} from "react-icons/md";

import { useFormik } from "formik";
import { SocialMediaDetails } from "../../../utils/constants";
import "../../Admin/influencerModal.css";
import { DropDown } from "../../Common/Components/DropDown";
import { InputLabel } from "../../Common/Components/InputLabel";
import { InputWithLabel } from "../../Common/Components/InputWithLabel";
import { NumberField } from "../../Common/Components/NumberField";
import axiosMain from "../../http/axiosMain";
import { influencerSchema } from "./schema";
import { showErrorToast, showSuccessToast } from "../../../utils/functions";

const socialLinksMaster = [
  "instagram",
  "youtube",
  "facebook",
  "linkedin",
  "snapchat",
  "twitch",
  "twitter",
  "reddit",
  "quora",
];

const initialValues = {
  firstName: "",
  lastName: "",
  age: "",
  category: [],
  phoneNo: "",
  email: "",
  address: "",
  socialLinks: socialLinksMaster.reduce((acc, key) => {
    return {
      ...acc,
      [key]: "",
      [`${key}Verified`]: false,
    };
  }, {}),
};

function InfluencerModal({ open, handleClose }) {
  const [categories, setCategories] = useState([]);
  const [socialLoading, setSocialLoading] = useState(null);

  const fetchCategories = async () => {
    try {
      const response = await axiosMain.get("/api/v1/get-categories");
      if (
        response.data?.categories &&
        Array.isArray(response.data.categories)
      ) {
        setCategories(
          response.data.categories.map((i) => ({ label: i, value: i }))
        );
      } else {
        console.error("Invalid category format received:", response.data);
        setCategories([]); // Fallback to empty array
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
      setCategories([]); // Fallback to empty array
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const addInfluencer = () => {
    setFieldValue("influencers", [...values.influencers, initialValues]);
  };

  const removeInfluencer = (idx) => {
    setFieldValue(
      "influencers",
      values.influencers.filter((_, index) => index !== idx)
    );
  };

  const {
    values,
    handleChange,
    errors,
    setFieldError,
    setFieldValue,
    handleSubmit,
  } = useFormik({
    initialValues: {
      influencers: [initialValues],
    },
    validateOnChange: false,
    validationSchema: influencerSchema,
    onSubmit: async (data) => {
      try {
        const response = await axiosMain.post(
          "/api/admin/influencers/bulk-influencers",
          data
        );
        if (response.data?.success) {
          if (response.data?.resp?.length)
            showSuccessToast(
              `${response.data?.resp?.length} Influencer(s) added successfully.`
            );
          handleClose();
        } else {
          showErrorToast("Something went wrong.");
        }
      } catch (err) {
        console.log("err", err.message);
        showErrorToast(
          err?.response?.data?.message || err.message || "Something went wrong."
        );
      }
    },
  });

  const validateInstagramURL = async (index, type, username) => {
    const platformKey = `influencers.[${index}].socialLinks[${type}]`;
    const platformVerifiedKey = `influencers[${index}].socialLinks[${type}Verified]`;
    try {
      const usernameRegex = /^@[A-Za-z0-9._]{1,29}$/;

      // If no error after validation, send email
      const isValid = usernameRegex.test(username);
      if (isValid) {
        setSocialLoading(type);
        const response = await axiosMain.post(
          "/api/statistics/instagram/validate-username",
          { type, username }
        );
        if (response?.data?.success === false) {
          setFieldValue(platformVerifiedKey, false);
          setFieldError(
            platformKey,
            response.data.message || "Validation failed"
          );
        } else {
          if (response?.data?.data?.id) {
            setFieldValue(platformVerifiedKey, true);
            setFieldError(platformKey, ""); // Clear previous error
          } else {
            setFieldValue(platformVerifiedKey, false);
            setFieldError(platformKey, "Invalid username.");
          }
        }
      } else {
        setFieldValue(platformVerifiedKey, false);
        setFieldError(platformKey, "Instagram Profile is invalid.");
      }
    } catch (error) {
      console.error("Error validating instagram username:", error);
      setFieldValue(platformVerifiedKey, false);
      setFieldError(
        platformKey,
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Something went wrong"
      );
    } finally {
      setSocialLoading(false);
    }
  };

  const validateYoutTubeURL = async (index, type, username) => {
    const platformKey = `influencers[${index}].socialLinks[${type}]`;
    const platformVerifiedKey = `influencers[${index}].socialLinks[${type}Verified]`;
    try {
      const usernameRegex = /^@[A-Za-z0-9._]{1,29}$/;

      // If no error after validation, send email
      const isValid = usernameRegex.test(username);
      if (isValid) {
        setSocialLoading(type);
        const response = await axiosMain.post(
          "/api/statistics/youtube/validate-username",
          { type, username }
        );

        if (response?.data?.success === false) {
          setFieldValue(platformVerifiedKey, false);
          setFieldError(
            platformKey,
            response.data.message || "Validation failed"
          );
        } else {
          if (response?.data?.data?.id) {
            setFieldValue(platformVerifiedKey, true);
            setFieldError(platformKey, ""); // Clear previous error
          } else {
            setFieldValue(platformVerifiedKey, false);
            setFieldError(platformKey, "Invalid category format received.");
          }
        }
      } else {
        setFieldValue(platformVerifiedKey, false);
        setFieldError(platformKey, "Youtube Profile is invalid.");
      }
    } catch (error) {
      console.error("Error validating youtube username:", error);
      setFieldValue(platformVerifiedKey, false);
      setFieldError(
        platformKey,
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Something went wrong"
      );
    } finally {
      setSocialLoading(false);
    }
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "75vw",
          maxHeight: "90vh",
          overflowY: "auto",
          bgcolor: "background.paper",
          borderRadius: 2,
          boxShadow: 24,
          p: 4,
        }}
      >
        {/* Close Icon */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
          mb={3}
        >
          <Box>
            {/* <Typography variant="h6">Media Upload</Typography>
            <Typography variant="body2">
              Add your documents here, and you can upload up to 5 files max
            </Typography> */}
          </Box>
          <IconButton
            onClick={handleClose}
            sx={{ color: "#888", border: "none !important" }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Media Upload Placeholder */}
        {/* <Grid container spacing={3}>
          <Grid size={12}>
            <Box
              p={3}
              sx={{
                border: "1px dashed #6B8E76",
                borderRadius: "1rem",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
              }}
              rowGap={0.5}
            >
              <MdOutlineCloudUpload size={40} color="#6b8e76" />
              <Typography fontSize={"14px"}>
                Drag your file(s) or{" "}
                <span
                  style={{
                    fontWeight: 600,
                    color: "#6b8e76",
                    cursor: "pointer",
                  }}
                >
                  Browse
                </span>
              </Typography>
              <Typography fontSize={"12px"}>
                Max 10 MB files are allowed
              </Typography>
            </Box>
          </Grid>
        </Grid> */}

        {/* Add Influencer Header */}
        <Box
          display="flex"
          justifyContent="space-between"
          mb={2}
          mt={3}
          alignItems={"center"}
        >
          <Typography variant="h6" className="add-influencer">
            Add Influencer
          </Typography>
          <Typography
            className="increment-influencer"
            sx={{ cursor: "pointer" }}
            onClick={addInfluencer}
            fontWeight={600}
          >
            + Add Influencer
          </Typography>
        </Box>

        {/* Influencer Form Sections */}
        <Grid container spacing={3}>
          {values.influencers.map((_, index) => (
            <Grid size={12} key={index}>
              <Accordion
                defaultExpanded={index === 0}
                sx={{
                  boxShadow: "none",
                  border: Object.keys(errors.influencers?.[index] || {}).length
                    ? "2px solid #d32f2f"
                    : "2px solid #EEEEEE",
                  borderRadius: "1rem !important",
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{ borderRadius: "1rem", border: "none !important" }}
                >
                  <Box
                    display={"flex"}
                    alignItems={"center"}
                    justifyContent={"space-between"}
                    width={"100%"}
                    marginRight={"1rem"}
                  >
                    <Typography
                      className="influencer-modal-text"
                      variant="subtitle1"
                      fontWeight={600}
                    >
                      Influencer {index + 1}
                    </Typography>
                    {values.influencers.length > 1 ? (
                      <MdOutlineDeleteOutline
                        size={20}
                        onClick={(e) => {
                          e.stopPropagation();
                          removeInfluencer(index);
                        }}
                      />
                    ) : (
                      <></>
                    )}
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={3}>
                    <Grid size={12}>
                      <Typography
                        className="basic-details-influencer"
                        variant="h6"
                        fontWeight={600}
                      >
                        Basic Details
                      </Typography>
                    </Grid>
                    <Grid size={6}>
                      <InputWithLabel
                        name={`influencers[${index}].firstName`}
                        label="First Name"
                        placeholder="Enter First Name"
                        value={values.influencers[index].firstName}
                        onChange={handleChange}
                        error={errors?.influencers?.[index]?.firstName}
                      />
                    </Grid>

                    <Grid size={6}>
                      <InputWithLabel
                        name={`influencers[${index}].lastName`}
                        label="Last Name"
                        placeholder="Enter Last Name"
                        value={values.influencers[index].lastName}
                        onChange={handleChange}
                        error={errors?.influencers?.[index]?.lastName}
                      />
                    </Grid>
                    <Grid size={6}>
                      <Box
                        display={"flex"}
                        alignItems={"start"}
                        justifyContent={"center"}
                        flexDirection={"column"}
                        rowGap={0.5}
                      >
                        <InputLabel label={"Age"} />
                        <NumberField
                          placeholder={"Enter Age"}
                          error={errors?.influencers?.[index]?.age}
                          value={values.influencers[index].age}
                          maxLength={2}
                          handleChange={handleChange}
                          setError={setFieldError}
                          name={`influencers[${index}].age`}
                        />
                      </Box>
                    </Grid>
                    <Grid size={6}>
                      <Box
                        display={"flex"}
                        alignItems={"start"}
                        justifyContent={"center"}
                        flexDirection={"column"}
                        rowGap={0.5}
                      >
                        <InputLabel label={"Select Category(ies)"} />
                        <DropDown
                          name={`influencers[${index}].category`}
                          label={"Select Category(ies)"}
                          options={categories}
                          multiple
                          onChange={handleChange}
                          value={values.influencers[index].category}
                          error={Boolean(
                            !!errors?.influencers?.[index]?.category
                          )}
                          helperText={errors?.influencers?.[index]?.category}
                        />
                      </Box>
                    </Grid>
                    <Grid size={6}>
                      <Box
                        display={"flex"}
                        alignItems={"start"}
                        justifyContent={"center"}
                        flexDirection={"column"}
                        rowGap={0.5}
                      >
                        <InputLabel label={"Phone Number"} />
                        <NumberField
                          placeholder={"Enter Phone Number"}
                          error={errors?.influencers?.[index]?.phoneNo}
                          value={values.influencers[index].phoneNo}
                          maxLength={10}
                          handleChange={handleChange}
                          setError={setFieldError}
                          name={`influencers[${index}].phoneNo`}
                        />
                      </Box>
                    </Grid>
                    <Grid size={6}>
                      <InputWithLabel
                        label={"Email Address"}
                        value={values.influencers[index].email}
                        onChange={handleChange}
                        name={`influencers[${index}].email`}
                        error={errors?.influencers?.[index]?.email}
                        placeholder={"Enter Email Address"}
                      />
                    </Grid>
                    <Grid size={12}>
                      <InputWithLabel
                        label={"Address Line"}
                        value={values.influencers[index].address}
                        onChange={handleChange}
                        name={`influencers[${index}].address`}
                        error={errors?.influencers?.[index]?.address}
                        placeholder={"Enter Address"}
                      />
                    </Grid>
                    <Grid size={12}>
                      <Box>
                        <InputLabel label={"Social Media Handles"} />
                        <Grid container spacing={3}>
                          <Grid size={12}>
                            <Grid
                              container
                              width={"100%"}
                              spacing={2}
                              marginTop={"0.5rem"}
                            >
                              {Object.keys(
                                values.influencers[index].socialLinks
                              ).map((link) => {
                                if (!socialLinksMaster.includes(link)) {
                                  return <></>;
                                }

                                const { icon, key, placeholder } =
                                  SocialMediaDetails[link];
                                const platformKey = `influencers[${index}].socialLinks[${link}]`;
                                const platformVerifiedKey = `influencers[${index}].socialLinks[${link}Verified]`;
                                const platformValue =
                                  values?.influencers?.[index]?.socialLinks?.[
                                    link
                                  ];
                                const platformVerifiedValue =
                                  values?.influencers?.[index]?.socialLinks?.[
                                    `${link}Verified`
                                  ];

                                return (
                                  <Grid size={{ xs: 6, md: 4 }} key={key}>
                                    <TextField
                                      size="small"
                                      placeholder={placeholder}
                                      name={`influencers[${index}].socialLinks[${link}]`}
                                      value={
                                        values.influencers[index].socialLinks[
                                          link
                                        ]
                                      }
                                      onChange={(e) => {
                                        setFieldValue(
                                          platformVerifiedKey,
                                          false
                                        );
                                        setFieldError(platformKey, null);
                                        setSocialLoading(false);
                                        handleChange(e);
                                      }}
                                      sx={{
                                        width: "100%",
                                        "& .MuiInputBase-root": {
                                          height: "48px",
                                        },
                                      }}
                                      slotProps={{
                                        input: {
                                          startAdornment: (
                                            <InputAdornment position="start">
                                              {icon({ size: "24px" })}
                                            </InputAdornment>
                                          ),
                                          endAdornment: (
                                            <InputAdornment position="end">
                                              {platformValue &&
                                              !platformVerifiedValue &&
                                              socialLoading !== link ? (
                                                <Typography
                                                  fontSize={"12px"}
                                                  fontWeight={500}
                                                  sx={{
                                                    cursor: "pointer",
                                                  }}
                                                  onClick={() => {
                                                    if (link === "instagram") {
                                                      validateInstagramURL(
                                                        index,
                                                        link,
                                                        platformValue
                                                      );
                                                    } else if (
                                                      link === "youtube"
                                                    ) {
                                                      validateYoutTubeURL(
                                                        index,
                                                        link,
                                                        platformValue
                                                      );
                                                    }
                                                  }}
                                                >
                                                  Connect
                                                </Typography>
                                              ) : (
                                                <></>
                                              )}
                                              {platformValue &&
                                              platformVerifiedValue ? (
                                                <MdVerified color="green" />
                                              ) : (
                                                <></>
                                              )}
                                              {platformValue &&
                                              !platformVerifiedValue &&
                                              socialLoading === link ? (
                                                <CircularProgress
                                                  color="green"
                                                  size={12}
                                                />
                                              ) : (
                                                <></>
                                              )}
                                            </InputAdornment>
                                          ),
                                        },
                                      }}
                                      error={
                                        Boolean(
                                          !!errors?.influencers?.[index]
                                            ?.socialLinks?.[link]
                                        ) ||
                                        Boolean(
                                          !!errors?.influencers?.[index]
                                            ?.socialLinks?.[`${link}Verified`]
                                        )
                                      }
                                      helperText={
                                        errors?.influencers?.[index]
                                          ?.socialLinks?.[link] ||
                                        errors?.influencers?.[index]
                                          ?.socialLinks?.[`${link}Verified`]
                                      }
                                    />
                                  </Grid>
                                );
                              })}
                            </Grid>
                          </Grid>
                        </Grid>
                      </Box>
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
            </Grid>
          ))}
        </Grid>

        {/* Upload Button */}
        <Box
          display="flex"
          justifyContent={"space-between"}
          alignItems={"center"}
          mt={4}
        >
          <Box>
            {errors?.influencers && typeof errors?.influencers === "string" ? (
              <FormHelperText error={true} sx={{ fontSize: "1rem" }}>
                {errors?.influencers}
              </FormHelperText>
            ) : (
              <></>
            )}
          </Box>
          <Button variant="contained" onClick={() => handleSubmit()}>
            Upload
          </Button>
        </Box>
      </Box>
    </Modal>
  );
}

export default InfluencerModal;
